import CloseIcon from '@mui/icons-material/Close';
import { TagComplianceOverlayItem } from '../../../utils/tagComplianceOverlay';

type TagComplianceDetailsDialogProps = {
  item: TagComplianceOverlayItem | null;
  onClose: () => void;
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-gray-100 last:border-b-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-xs font-medium text-gray-900 text-right">{value}</span>
  </div>
);

export const TagComplianceDetailsDialog = ({ item, onClose }: TagComplianceDetailsDialogProps) => {
  if (!item) {
    return null;
  }

  const { shelf } = item;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {item.kind === 'orphan' ? 'Orphan price tag' : 'Tag compliance shelf'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {item.kind === 'orphan' && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Orphan tag</div>
              <Row label="OCR text" value={item.tag.ocr_text ?? '—'} />
              <Row
                label="OCR confidence"
                value={
                  item.tag.ocr_confidence != null ? Number(item.tag.ocr_confidence).toFixed(3) : '—'
                }
              />
              <Row label="Best containment" value={item.tag.containment ?? '—'} />
              <Row
                label="Coords"
                value={`${item.tag.x1}, ${item.tag.y1} → ${item.tag.x2}, ${item.tag.y2}`}
              />
              <p className="text-[11px] text-gray-500 pt-1">
                Detected by PR but counted by no shelf — it failed the ROI containment threshold
                everywhere. Reported for measurement only; it does not change the counts below.
              </p>
            </div>
          )}

          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">
              Shelf {item.kind === 'orphan' ? '(probable home)' : ''}
            </div>
            <Row label="Tag name" value={shelf.tagName} />
            <Row label="Mod / shelf" value={`${shelf.mod} / ${shelf.shelf}`} />
            <Row label="Mod coverage ID" value={shelf.modCoverageId} />
            <Row label="Price tags counted" value={shelf.priceTags} />
            <Row label="POG positions" value={shelf.pogPositions} />
            <Row label="Distinct products" value={shelf.distinctProducts} />
            <Row label="Subtask" value={shelf.subtaskType ?? '—'} />
            <Row label="Count" value={shelf.count ?? '—'} />
            <Row label="Price check" value={shelf.priceCheck ? 'yes' : 'no'} />
            <Row label="Skip reason" value={shelf.skipReason ?? '—'} />
            <Row label="Clipstrip present" value={shelf.clipstripPresent ? 'yes' : 'no'} />
            <Row label="Clipstrip credit" value={shelf.clipstripCredit ?? '—'} />
            <Row label="Midshelf covered gaps" value={shelf.midshelfCoveredGaps} />
            <Row label="Midshelf credit" value={shelf.midshelfCredit ?? '—'} />
            <Row label="Orphan tags on shelf" value={shelf.orphanPriceTags.length} />
            <Row label="Template boxes" value={shelf.templateBoxCoords.length} />
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-blue-600">Raw JSON</summary>
            <pre className="mt-2 bg-gray-900 text-gray-100 text-[11px] rounded p-3 overflow-auto max-h-64">
              {JSON.stringify(item.kind === 'orphan' ? item.tag : shelf, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
};
